/**
 * Pure helpers for the Communications screen: grouping messages into
 * conversations by the server's `thread_key`, filtering, counting, and wording
 * a message's delivery state.
 */
import type { DeliveryStatus, InboxFilter, InboxMessage } from '../types/Communications';

export interface InboxThread {
  /** React key / selection id: the server thread_key, or `msg:<id>` for a standalone note. */
  key: string;
  /** The server thread_key; null for a standalone message (not mutable, no bulk read). */
  threadKey: string | null;
  /** Newest first. */
  items: InboxMessage[];
  latest: InboxMessage;
  label: string;
  contact: InboxMessage['contact'];
  muted: boolean;
  unread: number;
  isWhatsApp: boolean;
}

export const FILTER_OPTIONS: Array<{ value: InboxFilter; label: string }> = [
  { value: 'all', label: 'All messages' },
  { value: 'unread', label: 'Unread' },
  { value: 'note', label: 'Notes' },
  { value: 'vendor', label: 'Vendors' },
  { value: 'guest', label: 'Guests' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

/** Raw delivery-receipt rows are provider noise, not conversation. */
const isEvent = (m: InboxMessage) => m.type === 'whatsapp_webhook_event';

const isWa = (m: InboxMessage) => m.source === 'whatsapp' || m.type === 'whatsapp';

export function messageMatchesFilter(m: InboxMessage, filter: InboxFilter): boolean {
  switch (filter) {
    case 'unread': return !m.read;
    case 'note': return m.type === 'note' && !isWa(m);
    case 'vendor': return m.type === 'vendor' && !isWa(m);
    case 'guest': return (m.type === 'guest' && !isWa(m)) || m.contact?.type === 'guest';
    case 'whatsapp': return isWa(m);
    default: return true;
  }
}

/** The conversation's display name. Never falls back to a raw JID. */
export function threadLabel(items: InboxMessage[]): string {
  const withContact = items.find(m => m.contact?.name);
  if (withContact?.contact) return withContact.contact.name;
  const latest = items[0];
  if (isWa(latest)) {
    for (const m of items) {
      const match = m.subject?.match(/^Reply from\s+(.+)$/i);
      if (match?.[1]?.trim()) return match[1].trim();
    }
    for (const m of items) if (m.wa_sender && !m.wa_sender.includes('@')) return m.wa_sender;
    for (const m of items) if (m.recipient && !m.recipient.includes('@')) return m.recipient;
    return 'WhatsApp contact';
  }
  return latest.recipient || latest.subject || 'Note';
}

export function buildThreads(messages: InboxMessage[], mutedThreads: string[]): InboxThread[] {
  const muted = new Set(mutedThreads);
  const sorted = messages
    .filter(m => !isEvent(m))
    .slice()
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));
  const order: string[] = [];
  const groups = new Map<string, InboxMessage[]>();
  for (const m of sorted) {
    const key = m.thread_key ?? `msg:${m.id}`;
    const list = groups.get(key);
    if (list) list.push(m);
    else { groups.set(key, [m]); order.push(key); }
  }
  return order.map(key => {
    const items = groups.get(key)!;
    const threadKey = items[0].thread_key;
    return {
      key,
      threadKey,
      items,
      latest: items[0],
      label: threadLabel(items),
      contact: items.find(m => m.contact)?.contact ?? null,
      muted: (threadKey !== null && muted.has(threadKey)) || items.some(m => m.muted),
      unread: items.filter(m => !m.read).length,
      isWhatsApp: items.some(isWa),
    };
  });
}

/** A conversation is shown when any of its messages matches; the pane still shows all of them. */
export function filterThreads(threads: InboxThread[], filter: InboxFilter): InboxThread[] {
  return filter === 'all' ? threads : threads.filter(t => t.items.some(m => messageMatchesFilter(m, filter)));
}

export interface InboxMetrics {
  total: number;
  unread: number;
  sent: number;
  failed: number;
}

export function isFailed(m: InboxMessage): boolean {
  return m.delivery_status === 'failed' || m.status === 'failed';
}

export function inboxMetrics(messages: InboxMessage[]): InboxMetrics {
  const real = messages.filter(m => !isEvent(m));
  return {
    total: real.length,
    unread: real.filter(m => !m.read).length,
    sent: real.filter(m => m.direction === 'outbound' && !isFailed(m) && m.status !== 'draft').length,
    failed: real.filter(isFailed).length,
  };
}

export type DeliveryTone = 'success' | 'info' | 'neutral' | 'danger' | 'warning';

export interface DeliveryInfo {
  status: DeliveryStatus | 'draft';
  label: string;
  tone: DeliveryTone;
  /** Why it failed, for a failed message. */
  error: string | null;
}

const DELIVERY_LABELS: Record<Exclude<DeliveryStatus, 'failed'>, { label: string; tone: DeliveryTone }> = {
  submitted: { label: 'Submitted', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'info' },
  delivered: { label: 'Delivered', tone: 'success' },
  read: { label: 'Read', tone: 'success' },
};

/** Delivery state of one message, or null when there is nothing to show (inbound, plain note). */
export function deliveryInfo(m: InboxMessage): DeliveryInfo | null {
  if (isFailed(m)) {
    return { status: 'failed', label: 'Failed', tone: 'danger', error: m.delivery_error ?? m.error_reason ?? null };
  }
  if (m.status === 'draft') return { status: 'draft', label: 'Draft', tone: 'warning', error: null };
  if (m.delivery_status && m.delivery_status in DELIVERY_LABELS) {
    const d = DELIVERY_LABELS[m.delivery_status as Exclude<DeliveryStatus, 'failed'>];
    return { status: m.delivery_status, label: d.label, tone: d.tone, error: null };
  }
  return null;
}

/** How a thread is answered: a linked guest, else a stored JID. Null when it cannot be replied to. */
export type ReplyTarget =
  | { contactType: 'guest'; contactId: string }
  | { jid: string };

export function replyTarget(thread: InboxThread): ReplyTarget | null {
  if (thread.contact) return { contactType: 'guest', contactId: thread.contact.id };
  const jid = thread.items.find(m => m.jid)?.jid;
  return jid ? { jid } : null;
}
